// UnifiedPortfolioApp : intégration complète Zero-Custody
import React, { useState, useEffect } from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { UnifiedPortfolioTable } from './features/portfolio/UnifiedPortfolioTable';
import InsightCardsSection from './InsightCardsSection';
import PortfolioInsightsPanel from './PortfolioInsightsPanel';
import RegimeBadge from './features/macro/RegimeBadge';
import MiniDominanceGraph from './features/macro/MiniDominanceGraph';
import { getDominanceHistory } from './services/macroMarketHistoryService';
import { detectMarketRegime } from './engines/marketRegimeEngine';
import { useMacroMarketStore } from './stores/macroMarketStore';
import { usePortfolioStore } from './stores/portfolioStore';

type SectionIntroProps = {
    eyebrow: string;
    title: string;
    description: string;
};

const SectionIntro: React.FC<SectionIntroProps> = ({ eyebrow, title, description }) => (
    <Box sx={{ mb: 1.5 }}>
        <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em' }}>
            {eyebrow}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', mb: 0.7 }}>
            {title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8, maxWidth: 760 }}>
            {description}
        </Typography>
    </Box>
);

const UnifiedPortfolioApp = () => {
        const setMacro = useMacroMarketStore(state => state.setState);
        const macro = useMacroMarketStore();
        const [regimeAnalysis, setRegimeAnalysis] = useState<any>(null);
        const portfolioInsights = usePortfolioStore((state) => state.insights);

        useEffect(() => {
            // Récupère l'historique et détecte le régime
            console.log('[FRONT] getDominanceHistory: calling...');
            getDominanceHistory().then(history => {
                console.log('[FRONT] getDominanceHistory result:', history);
                const analysis = detectMarketRegime(history, macro.marketRegime || '');
                setRegimeAnalysis(analysis);
                setMacro({
                    marketRegime: analysis.currentRegime,
                    marketRegimeExplanation: analysis.explanation
                });
            });
            // eslint-disable-next-line
        }, []);
    return (
        <Stack spacing={4}>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
                <Box id="portfolio-macro-section" sx={{ flex: 0.9, minWidth: 0 }}>
                    <SectionIntro
                        eyebrow="Pulse Macro"
                        title="Dominance et changement de régime"
                        description="Cette zone isole le bruit macro le plus utile au portefeuille: évolution récente de la dominance et éventuel changement de régime détecté."
                    />
                    <Stack spacing={1.5}>
                        <MiniDominanceGraph />
                        {regimeAnalysis ? (
                            <RegimeBadge
                                regimeChanged={regimeAnalysis.regimeChanged}
                                changeStrength={regimeAnalysis.changeStrength}
                                explanation={regimeAnalysis.explanation}
                            />
                        ) : (
                            <Chip
                                label="Aucun changement de régime significatif détecté sur la fenêtre suivie"
                                sx={{ alignSelf: 'flex-start', bgcolor: '#eef4f6', color: '#17323c', fontWeight: 700 }}
                            />
                        )}
                    </Stack>
                </Box>

                <Box id="portfolio-priorities-section" sx={{ flex: 1, minWidth: 0 }}>
                    <SectionIntro
                        eyebrow="Priorités"
                        title="Ce qu’il faut traiter en premier"
                        description="Après le hero, cette zone détaille les signaux de risque et d’optimisation qui méritent une action rapide."
                    />
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 2.5 },
                            borderRadius: 5,
                            background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
                            border: '1px solid rgba(16,37,43,0.08)',
                        }}
                    >
                        <PortfolioInsightsPanel />
                    </Paper>
                </Box>
            </Stack>

            <Box id="portfolio-opportunities-section" sx={{ minWidth: 0 }}>
                <SectionIntro
                    eyebrow="Narration"
                    title="Insights consolidés et actions recommandées"
                    description="Cette zone garde une lecture plus narrative du portefeuille: implications concrètes et actions à traiter sans surcharge inutile."
                />
                {Array.isArray(portfolioInsights) && portfolioInsights.length > 0 ? (
                    <InsightCardsSection />
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 5,
                            background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
                            border: '1px solid rgba(16,37,43,0.08)',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 1 }}>
                            Aucun insight consolidé pour le moment
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8 }}>
                            Synchronise le portefeuille ou ajoute des positions manuelles pour alimenter les recommandations et la lecture IA de cette zone.
                        </Typography>
                    </Paper>
                )}
            </Box>

            <Box id="portfolio-allocation-section" sx={{ minWidth: 0 }}>
                <SectionIntro
                    eyebrow="Allocation"
                    title="Tableau unifié des positions"
                    description="Le tableau consolide les positions exchange, manuelles et on-chain dans un seul espace opératoire, avec synchronisation, filtres et édition rapide."
                />
                <Alert
                    severity="info"
                    sx={{
                        mb: 2.5,
                        borderRadius: 3,
                        alignItems: 'center',
                        background: 'rgba(27, 79, 114, 0.08)',
                        border: '1px solid rgba(27, 79, 114, 0.18)',
                    }}
                >
                    Les wallets on-chain se gèrent désormais dans l’écran Connexions. Ici, ils restent visibles dans l’allocation consolidée aux côtés des positions exchange et des ajouts manuels.
                </Alert>
                <UnifiedPortfolioTable />
            </Box>
        </Stack>
    );
};

export default UnifiedPortfolioApp;
